import { useState, useEffect } from 'react';
import { extractMemberData, MEMBER_TYPES } from '../utils/membersConfig';

/**
 * Resolves a member profile by bioGuideId.
 *
 * The page used to read everything off location.state, which only exists on a
 * history entry the app created itself. A typed, pasted, refreshed or
 * search-engine link is a fresh navigation with no state, so the header
 * rendered blank while the votes and bills panels loaded normally.
 *
 * The API exposes no single-member lookup - GET /members/ accepts only
 * current_member, order_by, limit and skip - so a cold load pulls the current
 * roster once and finds its member in it. House plus Senate is roughly 540
 * rows, so one padded request covers the set.
 *
 * When location.state IS present the fetch never fires, so clicking through
 * from a list page stays instant.
 *
 * @param {string} bioGuideId
 * @param {Object|null} seed - profile fields off location.state, or null
 * @param {string} routeChamber - chamber derived from the URL
 * @returns {{ member: Object|null, loading: boolean, error: Error|null }}
 */

const MEMBERS_ENDPOINT = 'https://api.govglance.org/members/';

const ROSTER_LIMIT = 600;

// Shared across mounts so a second cold load in the same session is free.
let rosterPromise = null;

const fetchRoster = (apiKey) => {
  if (rosterPromise) return rosterPromise;

  rosterPromise = fetch(
    `${MEMBERS_ENDPOINT}?current_member=true&limit=${ROSTER_LIMIT}&skip=0`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${apiKey}`,
      },
    }
  )
    .then((response) => {
      if (!response.ok) {
        throw new Error('Failed to fetch members');
      }
      return response.json();
    })
    .catch((err) => {
      // Drop the cache so the next attempt retries instead of replaying a
      // failure for the rest of the session.
      rosterPromise = null;
      throw err;
    });

  return rosterPromise;
};

// extractMemberData speaks fullName / imageUrl / addressInfo / websiteUrl,
// while the page reads name / imageSource / address / webURL. Translating here
// means the component sees one shape whether the visitor clicked through or
// landed cold.
const toPageShape = (raw, routeChamber) => {
  const memberType =
    routeChamber === 'Senate' ? MEMBER_TYPES.SENATE : MEMBER_TYPES.HOUSE;

  const data = extractMemberData(raw, memberType);
  if (!data) return null;

  return {
    name: data.fullName,
    imageSource: data.imageUrl,
    address: data.addressInfo,
    // Phone rides inside address_information on this upstream; normalizeAddress
    // on the page pulls it back out.
    phone: null,
    district: data.district,
    webURL: data.websiteUrl,
    chamber: data.chamber || routeChamber,
    startYear: data.startYear,
    stateName: data.stateName,
  };
};

const useMember = (bioGuideId, seed, routeChamber) => {
  const hasSeed = Boolean(seed && seed.name);

  const [member, setMember] = useState(hasSeed ? seed : null);
  const [loading, setLoading] = useState(!hasSeed);
  const [error, setError] = useState(null);

  const apiKey = process.env.REACT_APP_API_KEY;

  useEffect(() => {
    if (hasSeed || !bioGuideId) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);

    fetchRoster(apiKey)
      .then((roster) => {
        if (cancelled) return;

        const raw = Array.isArray(roster)
          ? roster.find((entry) => entry.bio_guide_id === bioGuideId)
          : null;

        if (!raw) {
          // A valid-looking id that matches nobody: most often a former member,
          // since the roster request filters on current_member.
          setMember(null);
          setError(new Error('Member not found'));
          return;
        }

        setMember(toPageShape(raw, routeChamber));
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Error fetching member:', err);
        setMember(null);
        setError(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [bioGuideId, hasSeed, routeChamber, apiKey]);

  return { member, loading, error };
};

export default useMember;